// Pure order-editor model shared by the OrderEditor UI, the Finalize action and
// the live validation scripts.
//
//  - `ORDER_KINDS` describes the per-resource differences of the legacy forms
//    (SaleForm / PurchaseForm / QuotationForm / ReturnOrderForm / RecurringSaleForm).
//  - `lineFromCatalogItem` mirrors `addToOrder` (OrderCommonMethods.js) for an item
//    returned by `sale_order_items/{id}` / `purchase_order_items/{id}`.
//  - `stateFromRecord` mirrors `formatRes` (mixins/Order.js) for `GET {res}/{id}`.
//  - `buildOrderPayload` mirrors the `items[]` mapping of `Form.js#submit`, i.e. the
//    exact shape `OrderDataService` reads (price|cost, quantity, discount, taxes ids,
//    allTaxes, tax_included, unit_id, item_unit_id, selected{…}).
//  - `computeTotals` re-implements OrderDataService/OrderItemService/TaxAction/
//    DiscountAction (incl. PHP number_format rounding) so the totals bar shows what
//    the server will store.

export type OrderResource = 'sales' | 'purchases' | 'quotations' | 'return_orders' | 'recurring_sales';
export type PriceField = 'price' | 'cost';

export type OrderKind = {
  resource: OrderResource;
  // Item value field (`price` for sale-like forms, `cost` for purchases).
  field: PriceField;
  // Party select: fixed, or decided by the return-order type.
  party: 'customer' | 'supplier' | 'byType';
  typeOptions: string[] | null;
  defaultType: string;
  hasDraft: boolean;
  hasShipping: boolean;
  hasPayment: boolean;
  hasDeductFromRegister: boolean;
  recurring: boolean;
  // Whether `selected{variations,portions,modifiers,serials}` is persisted by the
  // backend (QuotationRequest / ReturnOrderRequest / RecurringSaleRequest strip
  // `items.*.selected`, and quotation/recurring items have no such relations).
  richSelections: boolean;
  searchEndpoint: (query: string) => string;
  resolveEndpoint: (id: string) => string;
  // Custom-field entity (GET {resource}/create).
  customFieldResource: string;
};

const saleSearch = (q: string) => `items/search?q=${encodeURIComponent(q)}`;
const saleResolve = (id: string) => `sale_order_items/${id}`;

export const ORDER_KINDS: Record<OrderResource, OrderKind> = {
  sales: {
    resource: 'sales',
    field: 'price',
    party: 'customer',
    typeOptions: ['nett', 'layaway', 'on_account'],
    defaultType: 'nett',
    hasDraft: true,
    hasShipping: true,
    hasPayment: false,
    hasDeductFromRegister: false,
    recurring: false,
    richSelections: true,
    searchEndpoint: saleSearch,
    resolveEndpoint: saleResolve,
    customFieldResource: 'sales'
  },
  purchases: {
    resource: 'purchases',
    field: 'cost',
    party: 'supplier',
    // PurchaseFormComponent hides the type select; PurchaseRequest requires it ('nett').
    typeOptions: null,
    defaultType: 'nett',
    hasDraft: true,
    hasShipping: true,
    hasPayment: false,
    hasDeductFromRegister: false,
    recurring: false,
    richSelections: true,
    searchEndpoint: (q) => `items_wp/search?type=standard,service&q=${encodeURIComponent(q)}`,
    resolveEndpoint: (id) => `purchase_order_items/${id}`,
    customFieldResource: 'purchases'
  },
  quotations: {
    resource: 'quotations',
    field: 'price',
    party: 'customer',
    typeOptions: null,
    defaultType: '',
    hasDraft: false,
    hasShipping: true,
    hasPayment: false,
    hasDeductFromRegister: false,
    recurring: false,
    richSelections: false,
    searchEndpoint: saleSearch,
    resolveEndpoint: saleResolve,
    customFieldResource: 'quotations'
  },
  return_orders: {
    resource: 'return_orders',
    field: 'price',
    party: 'byType',
    typeOptions: ['sale', 'purchase'],
    defaultType: '',
    hasDraft: true,
    // The legacy ReturnOrderForm comments the shipping field out.
    hasShipping: false,
    hasPayment: false,
    hasDeductFromRegister: true,
    recurring: false,
    richSelections: false,
    searchEndpoint: saleSearch,
    resolveEndpoint: saleResolve,
    customFieldResource: 'return_orders'
  },
  recurring_sales: {
    resource: 'recurring_sales',
    field: 'price',
    party: 'customer',
    typeOptions: null,
    defaultType: '',
    hasDraft: true,
    hasShipping: true,
    hasPayment: false,
    hasDeductFromRegister: false,
    recurring: true,
    richSelections: false,
    searchEndpoint: saleSearch,
    resolveEndpoint: saleResolve,
    customFieldResource: 'recurring_sales'
  }
};

// Values must match the method names of Console/CreateRecurringSales.php
// (`$this->{$recurring_sale->repeat}($date)`): any other value crashes the cron.
export const RECURRING_REPEATS = ['daily', 'weekly', 'monthly', 'quarterly', 'semiannual', 'yearly', 'biennially'] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Tax = { id: string; name: string; code?: string; rate: number; compound: boolean; state?: boolean; same?: boolean };

export type Unit = { id: string; name: string; operator?: string | null; operation_value?: number | null; isBase?: boolean };

export type Promotion = {
  id: string;
  name: string;
  type: string;
  discount: number;
  discount_method?: string | null;
  quantity_to_buy?: number | null;
  amount_to_spend?: number | null;
};

export type VariationOption = { id: string; meta: Record<string, string>; price: number; cost: number; available: number };

export type SelectedVariation = {
  id: string;
  meta: Record<string, string>;
  price: number;
  cost: number;
  quantity: number;
  available: number;
};

export type ModifierOption = { id: string; name: string; price: number; cost: number };
export type Modifier = { id: string; title: string; show_as: string; options: ModifierOption[] };
export type SelectedModifier = { id: string; mId: string; title: string; option: string; price: number; cost: number; quantity: number };

export type PortionComponentItem = { id: string; item_id: string; name: string; variations: VariationOption[] };
export type PortionChoosable = { id: string; name: string; items: PortionComponentItem[] };
export type Portion = {
  id: string;
  name: string;
  price: number;
  cost: number;
  choosables: PortionChoosable[];
  essentials: PortionComponentItem[];
  portion_items: PortionComponentItem[];
};
export type SelectedPortion = {
  id: string;
  name: string;
  price: number;
  cost: number;
  quantity: number;
  choosables: Array<{ id: string; selected: string; variation_id: string | null }>;
  essentials: Array<{ id: string; item_id: string; variation_id: string | null }>;
  portion_items: Array<{ id: string; item_id: string; variation_id: string | null }>;
};

export type SerialRange = { number: string; till: string };

export type OrderLine = {
  key: string;
  // Existing order-item id (only when editing a stored order).
  id?: string;
  item_id: string;
  code: string;
  name: string;
  type?: string;
  price: number;
  cost: number;
  // Base value used for unit conversion (legacy `real_value`).
  baseValue: number;
  quantity: number;
  discount: number | null;
  // Selected tax ids (legacy `allTaxes`).
  allTaxes: string[];
  tax_included: number;
  unit_id: string | null;
  item_unit_id: string | null;
  units: Unit[];
  batch_no: string;
  expiry_date: string;
  comment: string;
  // Catalog constraints (from sale_order_items / purchase_order_items).
  changeable: boolean;
  expiry: boolean;
  min_price: number | null;
  max_price: number | null;
  max_discount: number | null;
  has_serials: boolean;
  serialOptions: Array<{ id: string; number: string }>;
  // Sales: chosen serial ids. Purchases: number/till ranges.
  serials: string[];
  serialRanges: SerialRange[];
  hasVariants: boolean;
  variations: VariationOption[];
  selectedVariations: SelectedVariation[];
  modifiers: Modifier[];
  selectedModifiers: SelectedModifier[];
  portions: Portion[];
  selectedPortions: SelectedPortion[];
  allPromotions: Promotion[];
  promotions: string[];
  // True once full catalog metadata is attached (lazy-loaded when editing).
  catalogLoaded: boolean;
  // Tax ids the server applied to a stored line (used by Finalize, which has no tax catalog).
  appliedTaxIds?: string[];
};

export type OrderForm = {
  id: string | null;
  type: string;
  date: string;
  start_date: string;
  repeat: string;
  create_before: string;
  customer_id: string;
  supplier_id: string;
  reference: string;
  taxes: string[];
  shipping: string;
  discount: string;
  discount_method: 'order' | 'items';
  details: string;
  draft: boolean;
  // Whether the stored record is (still) a draft: legacy `is_draftable()`.
  wasDraft: boolean;
  deduct_from_register: boolean;
  extra_attributes: Record<string, unknown>;
  items: OrderLine[];
};

// ---------------------------------------------------------------------------
// Number helpers (PHP number_format rounding: half away from zero)
// ---------------------------------------------------------------------------

export function num(value: unknown, fallback = 0): number {
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
}

export function round(value: number, decimals = 4): number {
  if (!Number.isFinite(value)) return 0;
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  // Exponent arithmetic avoids binary artefacts (e.g. 1.005 → 1.01).
  const shifted = Math.round(Number(`${abs}e${decimals}`) + Number.EPSILON);
  return sign * Number(`${shifted}e-${decimals}`);
}

function str(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function records(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object') : [];
}

let keySeed = 0;
export function lineKey() {
  keySeed += 1;
  return `l${Date.now().toString(36)}${keySeed}${Math.random().toString(36).slice(2, 7)}`;
}

export function today() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function dateOnly(value: unknown): string {
  const text = str(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
}

// ---------------------------------------------------------------------------
// Catalog mapping
// ---------------------------------------------------------------------------

export function toTax(raw: Record<string, unknown>): Tax {
  return {
    id: str(raw.id ?? raw.value),
    name: str(raw.name ?? raw.label),
    code: raw.code ? str(raw.code) : undefined,
    rate: num(raw.rate),
    compound: raw.compound === true || raw.compound === 1 || raw.compound === '1',
    state: raw.state === true || raw.state === 1 || raw.state === '1',
    same: raw.same === true || raw.same === 1 || raw.same === '1'
  };
}

function parseMeta(meta: unknown): Record<string, string> {
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    return Object.fromEntries(Object.entries(meta as Record<string, unknown>).map(([k, v]) => [k, str(v)]));
  }
  if (typeof meta === 'string' && meta.trim().startsWith('{')) {
    try {
      return parseMeta(JSON.parse(meta));
    } catch {
      return {};
    }
  }
  return {};
}

export function metaLabel(meta: Record<string, string>) {
  const entries = Object.entries(meta);
  return entries.length ? entries.map(([k, v]) => `${k}: ${v}`).join(', ') : '—';
}

function priceGroupPrice(raw: Record<string, unknown>): number | null {
  const group = records(raw.price_group)[0];
  const pivot = group?.pivot as Record<string, unknown> | undefined;
  return pivot?.price != null && pivot.price !== '' ? num(pivot.price) : null;
}

function toVariation(raw: Record<string, unknown>): VariationOption {
  const stock = records(raw.location_stock)[0] ?? {};
  const pg = priceGroupPrice(raw);
  return {
    id: str(raw.id),
    meta: parseMeta(raw.meta),
    price: pg ?? (stock.price != null && stock.price !== '' ? num(stock.price) : num(raw.price)),
    cost: stock.cost != null && stock.cost !== '' ? num(stock.cost) : num(raw.cost),
    available: num(stock.quantity)
  };
}

function toComponentItem(raw: Record<string, unknown>): PortionComponentItem {
  const item = (raw.item as Record<string, unknown> | undefined) ?? {};
  return {
    id: str(raw.id),
    item_id: str(raw.item_id ?? item.id),
    name: str(item.name ?? raw.name),
    variations: records(item.variations).map(toVariation)
  };
}

function toPortion(raw: Record<string, unknown>): Portion {
  const pg = priceGroupPrice(raw);
  return {
    id: str(raw.id),
    name: str(raw.name),
    price: pg ?? num(raw.price),
    cost: num(raw.cost),
    choosables: records(raw.choosables).map((g) => ({ id: str(g.id), name: str(g.name), items: records(g.items).map(toComponentItem) })),
    essentials: records(raw.essentials).map(toComponentItem),
    portion_items: records(raw.portion_items).map(toComponentItem)
  };
}

function toModifier(raw: Record<string, unknown>): Modifier {
  return {
    id: str(raw.id),
    title: str(raw.title ?? raw.name),
    show_as: str(raw.show_as || 'checkbox'),
    options: records(raw.options).map((o) => {
      const item = (o.item as Record<string, unknown> | undefined) ?? {};
      return { id: str(o.id), name: str(item.name ?? o.name), price: num(item.price ?? o.price), cost: num(item.cost ?? o.cost) };
    })
  };
}

function toPromotion(raw: Record<string, unknown>): Promotion {
  return {
    id: str(raw.id),
    name: str(raw.name),
    type: str(raw.type),
    discount: num(raw.discount),
    discount_method: raw.discount_method ? str(raw.discount_method) : null,
    quantity_to_buy: raw.quantity_to_buy != null ? num(raw.quantity_to_buy) : null,
    amount_to_spend: raw.amount_to_spend != null ? num(raw.amount_to_spend) : null
  };
}

function unitConversion(unit: Unit) {
  const value = num(unit.operation_value, 1);
  switch (unit.operator) {
    case '*':
      return value;
    case '/':
      return value ? 1 / value : 1;
    case '+':
      return 1 + value;
    case '-':
      return 1 - value;
    default:
      return 1;
  }
}

function toUnits(rawUnit: unknown): Unit[] {
  if (!rawUnit || typeof rawUnit !== 'object') return [];
  const unit = rawUnit as Record<string, unknown>;
  const base: Unit = { id: str(unit.id), name: str(unit.name), isBase: true };
  const subunits = records(unit.subunits).map((sub) => ({
    id: str(sub.id),
    name: str(sub.name),
    operator: sub.operator ? str(sub.operator) : null,
    operation_value: sub.operation_value != null ? num(sub.operation_value) : null
  }));
  return [base, ...subunits];
}

/** Value of the line for a given unit (legacy `calculateUnitPrice`). */
export function valueForUnit(line: OrderLine, unitId: string | null): number {
  const unit = line.units.find((u) => u.id === unitId);
  if (!unit || unit.isBase) return line.baseValue;
  return round(unitConversion(unit) * line.baseValue, 4);
}

function uniquePromotions(list: Promotion[]) {
  const seen = new Set<string>();
  return list.filter((p) => p.id && !seen.has(p.id) && seen.add(p.id));
}

/** Catalog item (sale_order_items / purchase_order_items) → line metadata. */
function catalogMeta(raw: Record<string, unknown>, field: PriceField) {
  const stock = records(raw.location_stock)[0] ?? {};
  const pg = priceGroupPrice(raw);
  const price = pg ?? (stock.price != null && stock.price !== '' ? num(stock.price) : num(raw.price));
  const cost = stock.cost != null && stock.cost !== '' ? num(stock.cost) : num(raw.cost);
  const categoryPromos = records(records(raw.categories)[0]?.valid_promotions).map(toPromotion);
  const ownPromos = records(raw.valid_promotions).map(toPromotion);
  return {
    type: raw.type ? str(raw.type) : undefined,
    price,
    cost,
    baseValue: field === 'price' ? price : cost,
    tax_included: num(raw.tax_included) ? 1 : 0,
    units: toUnits(raw.unit),
    changeable: Boolean(num(raw.changeable)),
    expiry: Boolean(num(raw.expiry)),
    min_price: raw.min_price != null && raw.min_price !== '' ? num(raw.min_price) : null,
    max_price: raw.max_price != null && raw.max_price !== '' ? num(raw.max_price) : null,
    max_discount: raw.max_discount != null && raw.max_discount !== '' ? num(raw.max_discount) : null,
    has_serials: Boolean(num(raw.has_serials)),
    serialOptions: records(raw.serials).map((s) => ({ id: str(s.id), number: str(s.number) })),
    hasVariants: Boolean(num(raw.has_variants)) && records(raw.variations).length > 0,
    variations: records(raw.variations).map(toVariation),
    modifiers: records(raw.modifiers).map(toModifier),
    portions: records(raw.portions).map(toPortion),
    allPromotions: uniquePromotions([...ownPromos, ...categoryPromos]),
    taxIds: records(raw.taxes).map((t) => str(t.id)),
    unitIdDefault: str(field === 'price' ? raw.sale_unit_id || raw.unit_id : raw.purchase_unit_id || raw.unit_id) || null,
    itemUnitId: str(raw.unit_id ?? (raw.unit as Record<string, unknown> | undefined)?.id) || null
  };
}

/** Whether adding this catalog item needs a selection step (legacy SelectVariationComponent). */
export function needsSelection(line: OrderLine, kind: OrderKind) {
  if (!kind.richSelections) return false;
  if (kind.field === 'cost') return line.hasVariants;
  return (line.has_serials && line.serialOptions.length > 0) || line.modifiers.length > 0 || line.portions.length > 0 || line.hasVariants;
}

/** legacy `addToOrder` for a new row. */
export function lineFromCatalogItem(
  raw: Record<string, unknown>,
  kind: OrderKind,
  options: { quantity?: number; price?: number; groupDiscount?: number } = {}
): OrderLine {
  const meta = catalogMeta(raw, kind.field);
  const quantity = options.quantity ?? 1;
  const line: OrderLine = {
    key: lineKey(),
    item_id: str(raw.id),
    code: str(raw.code),
    name: str(raw.name),
    type: meta.type,
    price: options.price ?? meta.price,
    cost: meta.cost,
    baseValue: options.price ?? meta.baseValue,
    quantity,
    discount: options.groupDiscount ? options.groupDiscount : null,
    allTaxes: meta.taxIds,
    tax_included: meta.tax_included,
    unit_id: meta.unitIdDefault,
    item_unit_id: meta.itemUnitId,
    units: meta.units,
    batch_no: '',
    expiry_date: '',
    comment: '',
    changeable: meta.changeable,
    expiry: meta.expiry,
    min_price: meta.min_price,
    max_price: meta.max_price,
    max_discount: meta.max_discount,
    has_serials: meta.has_serials,
    serialOptions: meta.serialOptions,
    serials: [],
    serialRanges: kind.field === 'cost' && meta.has_serials ? [{ number: '', till: '' }] : [],
    hasVariants: kind.richSelections && meta.hasVariants,
    variations: kind.richSelections ? meta.variations : [],
    selectedVariations: [],
    modifiers: kind.richSelections && kind.field === 'price' ? meta.modifiers : [],
    selectedModifiers: [],
    portions: kind.richSelections && kind.field === 'price' ? meta.portions : [],
    selectedPortions: [],
    allPromotions: kind.field === 'price' ? meta.allPromotions : [],
    promotions: [],
    catalogLoaded: true
  };
  // A sale/purchase unit different from the base unit converts the value.
  if (line.unit_id && line.unit_id !== line.item_unit_id && line.units.length) {
    line[kind.field] = valueForUnit(line, line.unit_id);
  }
  // Recipes with a single portion and no modifiers are added straight away.
  if (line.type === 'recipe' && line.portions.length === 1 && !line.modifiers.length) {
    line.selectedPortions = [newSelectedPortion(line.portions[0], quantity)];
  }
  return line;
}

/** Attach fresh catalog metadata to a line loaded from a stored order. */
export function attachCatalog(line: OrderLine, raw: Record<string, unknown>, kind: OrderKind): OrderLine {
  const meta = catalogMeta(raw, kind.field);
  return {
    ...line,
    type: meta.type,
    baseValue: line.unit_id && line.unit_id !== line.item_unit_id ? line.baseValue : meta.baseValue,
    units: line.units.length ? line.units : meta.units,
    changeable: meta.changeable,
    expiry: meta.expiry,
    min_price: meta.min_price,
    max_price: meta.max_price,
    max_discount: meta.max_discount,
    has_serials: meta.has_serials || line.has_serials,
    serialOptions: uniqueById([...line.serialOptions, ...meta.serialOptions]),
    hasVariants: kind.richSelections && (meta.hasVariants || line.selectedVariations.length > 0),
    variations: kind.richSelections ? meta.variations : [],
    modifiers: kind.richSelections && kind.field === 'price' ? meta.modifiers : [],
    portions: kind.richSelections && kind.field === 'price' ? meta.portions : [],
    allPromotions: kind.field === 'price' ? uniquePromotions([...line.allPromotions, ...meta.allPromotions]) : [],
    tax_included: meta.tax_included,
    catalogLoaded: true
  };
}

function uniqueById<T extends { id: string }>(list: T[]) {
  const seen = new Set<string>();
  return list.filter((entry) => !seen.has(entry.id) && seen.add(entry.id));
}

export function newSelectedPortion(portion: Portion, quantity = 1): SelectedPortion {
  return {
    id: portion.id,
    name: portion.name,
    price: portion.price,
    cost: portion.cost,
    quantity,
    choosables: portion.choosables.map((g) => ({
      id: g.id,
      selected: g.items[0]?.item_id ?? '',
      variation_id: g.items[0]?.variations[0]?.id ?? null
    })),
    essentials: portion.essentials.map((e) => ({ id: e.id, item_id: e.item_id, variation_id: e.variations[0]?.id ?? null })),
    portion_items: portion.portion_items.map((e) => ({ id: e.id, item_id: e.item_id, variation_id: e.variations[0]?.id ?? null }))
  };
}

/** legacy `checkPromos` / `applyPromoDiscount`: promotions applicable to a line. */
export function applicablePromotionIds(line: OrderLine, orderTotal: number): string[] {
  return line.allPromotions
    .filter((p) => {
      if (p.type === 'simple') return true;
      if (p.type === 'advance') return line.quantity >= num(p.quantity_to_buy);
      if (p.type === 'SXGD') return orderTotal >= num(p.amount_to_spend);
      // BXGY adds a free row in legacy; the free item is added manually here.
      return false;
    })
    .map((p) => p.id);
}

// ---------------------------------------------------------------------------
// Stored record → editor state (legacy formatRes)
// ---------------------------------------------------------------------------

function taxIdsFrom(value: unknown): string[] {
  if (Array.isArray(value))
    return value
      .map((entry) => (entry && typeof entry === 'object' ? str((entry as Record<string, unknown>).id) : str(entry)))
      .filter(Boolean);
  if (value && typeof value === 'object')
    return Object.values(value as Record<string, unknown>)
      .map(str)
      .filter(Boolean);
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      return taxIdsFrom(JSON.parse(value));
    } catch {
      return [];
    }
  }
  return [];
}

function pivotOf(raw: Record<string, unknown>) {
  return (raw.pivot as Record<string, unknown> | undefined) ?? {};
}

function pivotJson(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) return records(value);
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      return records(JSON.parse(value));
    } catch {
      return [];
    }
  }
  return [];
}

export function lineFromRecordItem(raw: Record<string, unknown>, kind: OrderKind, duplicate: boolean): OrderLine {
  const item = (raw.item as Record<string, unknown> | undefined) ?? {};
  const field = kind.field;
  const value = num(raw[field] ?? raw.price);
  const units = toUnits(item.unit ?? raw.unit);
  const itemUnitId = str((item.unit as Record<string, unknown> | undefined)?.id ?? item.unit_id) || null;
  const recordTaxes = taxIdsFrom(raw.item_taxes);
  const fallbackTaxes = taxIdsFrom(raw.taxes).length ? taxIdsFrom(raw.taxes) : taxIdsFrom(item.taxes);
  const catalogVariations = records(item.variations).map(toVariation);
  const selectedVariations: SelectedVariation[] = records(raw.variations).map((v) => {
    const pivot = pivotOf(v);
    const catalog = catalogVariations.find((c) => c.id === str(v.id));
    return {
      id: str(v.id),
      meta: parseMeta(v.meta),
      price: num(pivot.price ?? v.price),
      cost: num(pivot.cost ?? v.cost),
      quantity: num(pivot.quantity, 1),
      available: num(pivot.quantity) + (catalog?.available ?? 0)
    };
  });
  const catalogModifiers = records(item.modifiers).map(toModifier);
  const selectedModifiers: SelectedModifier[] = records(raw.modifier_options).map((o) => {
    const pivot = pivotOf(o);
    const mId = str(o.modifier_id ?? pivot.modifier_id);
    const modifier = catalogModifiers.find((m) => m.id === mId);
    const option = modifier?.options.find((opt) => opt.id === str(o.id));
    return {
      id: str(o.id),
      mId,
      title: modifier?.title ?? '',
      option: option?.name ?? str((o.item as Record<string, unknown> | undefined)?.name ?? o.name),
      price: num(pivot.price ?? option?.price),
      cost: num(pivot.cost ?? option?.cost),
      quantity: num(pivot.quantity, 1)
    };
  });
  const selectedPortions: SelectedPortion[] = records(raw.portions).map((p) => {
    const pivot = pivotOf(p);
    const pivotChoosables = pivotJson(pivot.choosables);
    const pivotEssentials = pivotJson(pivot.essentials);
    const pivotItems = pivotJson(pivot.portion_items);
    return {
      id: str(p.id),
      name: str(p.name),
      price: num(pivot.price ?? p.price),
      cost: num(pivot.cost ?? p.cost),
      quantity: num(pivot.quantity, 1),
      choosables: pivotChoosables.map((c) => ({
        id: str(c.id),
        selected: str(c.item_id ?? c.selected),
        variation_id: c.variation_id ? str(c.variation_id) : null
      })),
      essentials: pivotEssentials.map((e) => ({
        id: str(e.id),
        item_id: str(e.item_id),
        variation_id: e.variation_id ? str(e.variation_id) : null
      })),
      portion_items: pivotItems.map((e) => ({
        id: str(e.id),
        item_id: str(e.item_id),
        variation_id: e.variation_id ? str(e.variation_id) : null
      }))
    };
  });
  const serialRecords = records(raw.serials);
  const promotions = records(raw.promotions).map(toPromotion);
  const line: OrderLine = {
    key: lineKey(),
    id: duplicate ? undefined : str(raw.id) || undefined,
    item_id: str(raw.item_id ?? item.id),
    code: str(raw.code ?? item.code),
    name: str(raw.name ?? item.name),
    price: field === 'price' ? value : num(raw.price),
    cost: field === 'cost' ? value : num(raw.cost),
    baseValue: num(item[field], value) || value,
    quantity: num(raw.quantity, 1),
    discount: num(raw.discount) > 0 ? num(raw.discount) : null,
    allTaxes: recordTaxes.length ? recordTaxes : fallbackTaxes,
    tax_included: num(item.tax_included) ? 1 : 0,
    unit_id: str(raw.unit_id) || null,
    item_unit_id: itemUnitId,
    units,
    batch_no: str(raw.batch_no),
    expiry_date: dateOnly(raw.expiry_date),
    comment: str(raw.comment),
    changeable: false,
    expiry: Boolean(raw.expiry_date),
    min_price: null,
    max_price: null,
    max_discount: null,
    has_serials: serialRecords.length > 0,
    serialOptions: serialRecords.map((s) => ({ id: str(s.id), number: str(s.number) })),
    serials: field === 'price' ? serialRecords.map((s) => str(s.id)) : [],
    serialRanges: field === 'cost' ? serialRecords.map((s) => ({ number: str(s.number), till: '' })) : [],
    hasVariants: kind.richSelections && selectedVariations.length > 0,
    variations: kind.richSelections ? catalogVariations : [],
    selectedVariations: kind.richSelections ? selectedVariations : [],
    modifiers: kind.richSelections && field === 'price' ? catalogModifiers : [],
    selectedModifiers: kind.richSelections && field === 'price' ? selectedModifiers : [],
    portions: kind.richSelections && field === 'price' ? records(item.portions).map(toPortion) : [],
    selectedPortions: kind.richSelections && field === 'price' ? selectedPortions : [],
    allPromotions: field === 'price' ? uniquePromotions([...promotions, ...records(item.valid_promotions).map(toPromotion)]) : [],
    promotions: promotions.map((p) => p.id),
    catalogLoaded: false,
    appliedTaxIds: taxIdsFrom(raw.taxes)
  };
  // The unit-converted value is what was stored; keep the base for conversions.
  if (line.unit_id && line.unit_id !== line.item_unit_id) line.baseValue = num(item[field], line.baseValue);
  return line;
}

export function emptyOrderForm(kind: OrderKind, defaults: { customerId?: string } = {}): OrderForm {
  return {
    id: null,
    type: kind.defaultType,
    date: today(),
    start_date: today(),
    repeat: kind.recurring ? 'monthly' : '',
    create_before: '',
    customer_id: kind.party !== 'supplier' && kind.resource !== 'return_orders' ? (defaults.customerId ?? '') : '',
    supplier_id: '',
    reference: '',
    taxes: [],
    shipping: '',
    discount: '',
    discount_method: 'items',
    details: '',
    // Safety posture: new orders default to draft (no stock/journal side effects).
    draft: kind.hasDraft,
    wasDraft: true,
    deduct_from_register: false,
    extra_attributes: {},
    items: []
  };
}

/** legacy `formatRes`; `duplicate` clears id/reference and resets the date. */
export function stateFromRecord(record: Record<string, unknown>, kind: OrderKind, duplicate = false): OrderForm {
  const base = emptyOrderForm(kind);
  const isDraft = record.draft === undefined || record.draft === null ? kind.hasDraft : Boolean(num(record.draft));
  const extras =
    record.extra_attributes && typeof record.extra_attributes === 'object' && !Array.isArray(record.extra_attributes)
      ? (record.extra_attributes as Record<string, unknown>)
      : {};
  const orderDiscount = num(record.discount);
  let type = str(record.type) || kind.defaultType;
  if (kind.typeOptions && !kind.typeOptions.includes(type)) type = kind.defaultType || kind.typeOptions[0];
  if (!kind.typeOptions) type = kind.defaultType;
  return {
    ...base,
    id: duplicate ? null : str(record.id) || null,
    type,
    date: duplicate ? today() : dateOnly(record.date) || today(),
    start_date: dateOnly(record.start_date) || today(),
    repeat: str(record.repeat) || base.repeat,
    create_before: record.create_before != null ? str(record.create_before) : '',
    customer_id: str(record.customer_id),
    supplier_id: str(record.supplier_id),
    reference: duplicate ? '' : str(record.reference),
    taxes: taxIdsFrom(record.taxes),
    shipping: record.shipping != null && num(record.shipping) !== 0 ? String(num(record.shipping)) : '',
    discount: orderDiscount > 0 ? String(orderDiscount) : '',
    // Line discounts are stored per item; an order-level discount means "order" mode.
    discount_method: orderDiscount > 0 ? 'order' : 'items',
    details: str(record.details),
    draft: duplicate ? kind.hasDraft : kind.hasDraft && isDraft,
    wasDraft: duplicate ? true : isDraft,
    deduct_from_register: false,
    extra_attributes: { ...extras },
    items: records(record.items).map((item) => lineFromRecordItem(item, kind, duplicate))
  };
}

// ---------------------------------------------------------------------------
// Taxes
// ---------------------------------------------------------------------------

export type TaxContext = { partyState?: string | null; locationState?: string | null };

/** legacy `applicableTaxes`: state taxes apply only on same/different state. */
export function applicableTaxIds(ids: string[], taxes: Tax[], ctx: TaxContext): string[] {
  return ids.filter((id) => {
    const tax = taxes.find((t) => t.id === id);
    if (!tax) return true;
    if (tax.state && ctx.partyState && ctx.locationState) {
      const same = ctx.partyState === ctx.locationState;
      return tax.same ? same : !same;
    }
    return true;
  });
}

type TaxLine = { id: string; amount: number };

/** TaxAction::calculate */
export function calcTaxes(ids: string[], taxes: Tax[], amount: number, included: boolean): TaxLine[] {
  if (!ids.length) return [];
  const selected = taxes.filter((t) => ids.includes(t.id));
  const compound = selected.filter((t) => t.compound);
  const nonCompound = selected.filter((t) => !t.compound);
  const out: TaxLine[] = [];
  if (included) {
    let compoundTotal = 0;
    compound.forEach((t) => {
      const a = round((amount * t.rate) / (100 + t.rate));
      compoundTotal += a;
      out.push({ id: t.id, amount: a });
    });
    nonCompound.forEach((t) => out.push({ id: t.id, amount: round(((amount - compoundTotal) * t.rate) / (100 + t.rate)) }));
  } else {
    let nonCompoundTotal = 0;
    nonCompound.forEach((t) => {
      const a = round((amount * t.rate) / 100);
      nonCompoundTotal += a;
      out.push({ id: t.id, amount: a });
    });
    compound.forEach((t) => out.push({ id: t.id, amount: round(((amount + nonCompoundTotal) * t.rate) / 100) }));
  }
  return out;
}

/** DiscountAction::calculate (percentage unless method === 'fixed'). */
export function calcDiscount(discount: unknown, amount: number, method: string | null = 'percentage'): number {
  const d = num(discount);
  if (d && amount) {
    const value = String(method ?? '').toLowerCase() === 'fixed' ? Math.min(d, amount) : (amount * d) / 100;
    return round(value, 2);
  }
  return d;
}

// ---------------------------------------------------------------------------
// Totals (OrderDataService)
// ---------------------------------------------------------------------------

export type LineTotals = {
  value: number;
  discount_amount: number;
  total_discount_amount: number;
  tax_amount: number;
  total_tax_amount: number;
  unit_value: number;
  subtotal: number;
};

export type OrderTotals = {
  items: number;
  quantity: number;
  total: number;
  item_tax_amount: number;
  item_discount_amount: number;
  discount_amount: number;
  order_tax_amount: number;
  total_tax_amount: number;
  shipping: number;
  grand_total: number;
  lines: Record<string, LineTotals>;
};

/** OrderItemService for one selected sub-row (variation/portion/modifier). */
function subRow(
  sub: { price: number; cost: number; quantity: number },
  field: PriceField,
  lineDiscount: number | null,
  taxIds: string[],
  taxes: Tax[]
) {
  const vfield = num(sub[field]);
  const discount_amount = calcDiscount(lineDiscount, vfield);
  const net = vfield - discount_amount;
  const qty = num(sub.quantity);
  // OrderItemService reads `tax_included` from the sub-row, which never carries it.
  const taxLines = calcTaxes(taxIds, taxes, net, false);
  const tax_amount = round(taxLines.reduce((a, t) => a + t.amount, 0));
  const unit = round(net + tax_amount);
  return {
    value: vfield,
    discount_amount,
    tax_amount,
    total_tax_amount: round(tax_amount * qty),
    total_discount_amount: round(discount_amount * qty),
    unit,
    total: round(qty * unit)
  };
}

export function computeLine(line: OrderLine, kind: OrderKind, taxIds: string[], taxes: Tax[]): LineTotals {
  const field = kind.field;
  const qty = num(line.quantity);
  const rich = kind.richSelections;
  if (field === 'cost') {
    if (rich && line.selectedVariations.length) {
      const subs = line.selectedVariations.map((v) => subRow(v, field, line.discount, taxIds, taxes));
      const value = subs.reduce((a, s) => a + s.value, 0);
      const discount_amount = subs.reduce((a, s) => a + s.discount_amount, 0);
      const tax_amount = subs.reduce((a, s) => a + s.tax_amount, 0);
      return {
        value,
        discount_amount,
        total_discount_amount: subs.reduce((a, s) => a + s.total_discount_amount, 0),
        tax_amount,
        total_tax_amount: subs.reduce((a, s) => a + s.total_tax_amount, 0),
        unit_value: round(value - discount_amount + tax_amount),
        subtotal: round(
          subs.reduce((a, s) => a + s.total, 0),
          2
        )
      };
    }
    const value = num(line.cost);
    const discount_amount = calcDiscount(line.discount, value);
    let net = value - discount_amount;
    const taxLines = calcTaxes(taxIds, taxes, net, Boolean(line.tax_included));
    const taxSum = taxLines.reduce((a, t) => a + t.amount, 0);
    if (line.tax_included && taxLines.length) net -= taxSum;
    const tax_amount = taxLines.length ? round(taxSum) : 0;
    const unit = round(net + tax_amount);
    return {
      value,
      discount_amount,
      total_discount_amount: round(discount_amount * qty),
      tax_amount,
      total_tax_amount: round(tax_amount * qty),
      unit_value: unit,
      subtotal: round(qty * unit, 2)
    };
  }

  // Sale-like.
  let modPrice = 0;
  let modDiscount = 0;
  let modTax = 0;
  let modTotalTax = 0;
  let modTotalDiscount = 0;
  let modSubtotal = 0;
  if (rich) {
    line.selectedModifiers.forEach((m) => {
      const s = subRow(m, field, line.discount, taxIds, taxes);
      modPrice += s.value;
      modDiscount += s.discount_amount;
      modTax += s.tax_amount;
      modTotalTax += s.total_tax_amount;
      modTotalDiscount += s.total_discount_amount;
      modSubtotal += s.total;
    });
  }
  if (rich && (line.selectedVariations.length || line.selectedPortions.length)) {
    let price = modPrice;
    let tax_amount = modTax;
    let discount_amount = modDiscount;
    let total_tax_amount = modTotalTax;
    let total_discount_amount = modTotalDiscount;
    let subtotal = modSubtotal;
    line.selectedVariations.forEach((v) => {
      const s = subRow(v, field, line.discount, taxIds, taxes);
      price += s.value;
      tax_amount += s.tax_amount;
      discount_amount += s.discount_amount;
      total_tax_amount += s.total_tax_amount;
      total_discount_amount += s.total_discount_amount;
      subtotal += s.total;
    });
    line.selectedPortions.forEach((p) => {
      const s = subRow(p, field, line.discount, taxIds, taxes);
      price += s.unit;
      tax_amount += s.tax_amount;
      discount_amount += s.discount_amount;
      total_tax_amount += s.total_tax_amount;
      total_discount_amount += s.total_discount_amount;
      subtotal += s.total;
    });
    return {
      value: price,
      discount_amount,
      total_discount_amount,
      tax_amount,
      total_tax_amount,
      unit_value: round(price - discount_amount + tax_amount),
      subtotal: round(subtotal, 2)
    };
  }
  const basePrice = num(line.price);
  let discount_amount = calcDiscount(line.discount, basePrice);
  line.promotions.forEach((pid) => {
    const promo = line.allPromotions.find((p) => p.id === pid);
    if (promo && promo.type !== 'BXGY')
      discount_amount += calcDiscount(promo.discount, basePrice - discount_amount, promo.discount_method ?? 'percentage');
  });
  const price = basePrice + modPrice;
  discount_amount += modDiscount;
  const total_discount_amount = round(discount_amount * qty) + modTotalDiscount;
  let net = price - discount_amount;
  const taxLines = calcTaxes(taxIds, taxes, net, Boolean(line.tax_included));
  const taxSum = taxLines.reduce((a, t) => a + t.amount, 0);
  if (line.tax_included && taxLines.length) net -= taxSum;
  const tax_amount = taxLines.length ? round(taxSum) : 0;
  const unit = round(net + tax_amount);
  return {
    value: price,
    discount_amount,
    total_discount_amount,
    tax_amount,
    total_tax_amount: round(tax_amount * qty),
    unit_value: unit,
    subtotal: round(qty * unit, 2)
  };
}

/**
 * Line discount sent to the server. Switching to "order" mode clears the line
 * discounts in the UI (legacy watcher); stored line discounts are kept as-is.
 */
export function effectiveLineDiscount(line: OrderLine, _form: OrderForm): number | null {
  return line.discount;
}

export function computeTotals(form: OrderForm, kind: OrderKind, taxes: Tax[], ctx: TaxContext): OrderTotals {
  const lines: Record<string, LineTotals> = {};
  let total = 0;
  let itemTax = 0;
  let itemDiscount = 0;
  let quantity = 0;
  form.items.forEach((line) => {
    const effective = { ...line, discount: effectiveLineDiscount(line, form) };
    const result = computeLine(effective, kind, applicableTaxIds(line.allTaxes, taxes, ctx), taxes);
    lines[line.key] = result;
    total += result.subtotal;
    itemTax += result.total_tax_amount;
    itemDiscount += result.total_discount_amount;
    quantity += num(line.quantity);
  });
  total = round(total, 2);
  const item_tax_amount = round(itemTax, 2);
  const discount_amount = form.discount_method === 'order' ? calcDiscount(form.discount, total) : 0;
  const orderTaxes = calcTaxes(applicableTaxIds(form.taxes, taxes, ctx), taxes, total - discount_amount, false);
  const order_tax_amount = orderTaxes.length
    ? round(
        orderTaxes.reduce((a, t) => a + t.amount, 0),
        2
      )
    : 0;
  const shipping = kind.hasShipping ? num(form.shipping) : 0;
  return {
    items: form.items.length,
    quantity,
    total,
    item_tax_amount,
    item_discount_amount: round(itemDiscount, 2),
    discount_amount,
    order_tax_amount,
    total_tax_amount: round(item_tax_amount + order_tax_amount, 2),
    shipping,
    grand_total: round(total - discount_amount + order_tax_amount + shipping, 2),
    lines
  };
}

// ---------------------------------------------------------------------------
// Payload (Form.js#submit)
// ---------------------------------------------------------------------------

export type PayloadOptions = {
  taxes: Tax[];
  ctx: TaxContext;
  // Custom-field definitions loaded from GET {res}/create.
  customFieldSlugs?: string[];
  attachments?: File[];
  // Force a draft value (Finalize passes 0).
  draftOverride?: 0 | 1;
  // Re-send the taxes the server applied (no tax catalog / party state available).
  useStoredTaxes?: boolean;
};

function linePayload(line: OrderLine, form: OrderForm, kind: OrderKind, opts: PayloadOptions) {
  const field = kind.field;
  const discount = effectiveLineDiscount(line, form);
  const appliedTaxes =
    opts.useStoredTaxes && line.appliedTaxIds ? line.appliedTaxIds : applicableTaxIds(line.allTaxes, opts.taxes, opts.ctx);
  const selected: Record<string, unknown> = { modifiers: [], variations: [], portions: [] };
  if (kind.richSelections) {
    selected.modifiers = line.selectedModifiers.map((m) => ({ id: m.mId, selected: m.id, price: m.price, quantity: m.quantity }));
    selected.variations = line.selectedVariations.map((v) => ({
      id: v.id,
      [field]: v[field],
      quantity: v.quantity,
      taxes: appliedTaxes,
      allTaxes: line.allTaxes,
      item_taxes: line.allTaxes
    }));
    selected.portions = line.selectedPortions.map((p) => ({
      id: p.id,
      cost: p.cost,
      price: p.price,
      quantity: p.quantity,
      essentials: p.essentials,
      choosables: p.choosables,
      portion_items: p.portion_items
    }));
    if (field === 'price' && line.serials.length) selected.serials = line.serials;
    if (field === 'cost' && line.has_serials) selected.serials = line.serialRanges.filter((r) => r.number || r.till);
  }
  const quantity =
    kind.richSelections && line.selectedVariations.length
      ? line.selectedVariations.reduce((a, v) => a + num(v.quantity), 0)
      : kind.richSelections && line.selectedPortions.length
        ? line.selectedPortions.reduce((a, p) => a + num(p.quantity), 0)
        : num(line.quantity);
  const payload: Record<string, unknown> = {
    ...(line.id ? { id: line.id } : {}),
    item_id: line.item_id,
    code: line.code,
    name: line.name,
    [field]: num(line[field]),
    quantity,
    discount: discount ? discount : 0,
    taxes: appliedTaxes,
    allTaxes: line.allTaxes,
    tax_included: line.tax_included ? 1 : 0,
    selected,
    promotions: field === 'price' ? line.promotions : [],
    allPromotions: field === 'price' ? line.allPromotions.map((p) => p.id) : []
  };
  if (line.unit_id) payload.unit_id = line.unit_id;
  if (field === 'cost' && line.unit_id && line.item_unit_id) payload.item_unit_id = line.item_unit_id;
  if (line.batch_no) payload.batch_no = line.batch_no;
  if (line.expiry_date) payload.expiry_date = line.expiry_date;
  if (line.comment) payload.comment = line.comment;
  if (line.has_serials) payload.has_serials = 1;
  return payload;
}

export function buildOrderPayload(form: OrderForm, kind: OrderKind, opts: PayloadOptions): Record<string, unknown> {
  const multipart = Boolean(opts.attachments && opts.attachments.length);
  const payload: Record<string, unknown> = {
    reference: form.reference || '',
    details: form.details || '',
    // Order taxes: an explicit empty value lets updates clear them (multipart drops []).
    taxes: form.taxes.length ? form.taxes : multipart ? '' : [],
    // Only "order" mode sends an order-level discount; "items" mode carries it per line
    // (legacy sent both, which made OrderDataService discount twice).
    discount: form.discount_method === 'order' && num(form.discount) > 0 ? num(form.discount) : null,
    items: form.items.map((line) => linePayload(line, form, kind, opts))
  };
  if (kind.hasShipping) payload.shipping = form.shipping === '' ? null : num(form.shipping);
  if (kind.recurring) {
    payload.start_date = form.start_date;
    payload.repeat = form.repeat;
    payload.create_before = form.create_before === '' ? null : num(form.create_before);
  } else {
    payload.date = form.date;
  }
  if (kind.typeOptions || kind.defaultType) payload.type = form.type || kind.defaultType;
  if (kind.party === 'customer') payload.customer_id = form.customer_id;
  else if (kind.party === 'supplier') payload.supplier_id = form.supplier_id;
  else if (form.type === 'purchase') payload.supplier_id = form.supplier_id;
  else payload.customer_id = form.customer_id;
  if (kind.hasDraft) payload.draft = opts.draftOverride ?? (form.draft ? 1 : 0);
  if (kind.hasDeductFromRegister && !form.id) payload.deduct_from_register = form.deduct_from_register ? 1 : 0;
  const slugs = opts.customFieldSlugs ?? [];
  if (slugs.length) {
    payload.extra_attributes = Object.fromEntries(slugs.map((slug) => [slug, form.extra_attributes[slug] ?? '']));
  }
  if (multipart) payload.attachments = opts.attachments;
  return payload;
}

/** Validate the form like the legacy iView rules + item checks. Returns i18n keys. */
export function validateOrderForm(form: OrderForm, kind: OrderKind, maxDiscount: number): Record<string, string> {
  const errors: Record<string, string> = {};
  if (kind.typeOptions && !form.type) errors.type = 'orders.error.typeRequired';
  if (kind.recurring) {
    if (!form.start_date) errors.start_date = 'orders.error.required';
    if (!form.repeat) errors.repeat = 'orders.error.required';
  } else if (!form.date) errors.date = 'orders.error.required';
  const party = kind.party === 'byType' ? (form.type === 'purchase' ? 'supplier' : 'customer') : kind.party;
  if (party === 'customer' && !form.customer_id) errors.customer_id = 'orders.error.required';
  if (party === 'supplier' && !form.supplier_id) errors.supplier_id = 'orders.error.required';
  if (num(form.discount) > maxDiscount && form.discount !== '') errors.discount = 'orders.error.maxDiscount';
  if (!form.items.length) errors.items = 'orders.error.itemsRequired';
  return errors;
}

export function partyOf(form: OrderForm, kind: OrderKind): 'customer' | 'supplier' {
  return kind.party === 'byType' ? (form.type === 'purchase' ? 'supplier' : 'customer') : kind.party;
}

// ---------------------------------------------------------------------------
// Scale barcode (core/Mixins.js#parseScaleBarcode)
// ---------------------------------------------------------------------------

export type ScaleBarcodeSettings = {
  type?: string;
  total_digits?: number | string;
  item_code_start?: number | string;
  item_code_digits?: number | string;
  price_start?: number | string;
  price_digits?: number | string;
  price_divide_by?: number | string;
  weight_start?: number | string;
  weight_digits?: number | string;
  weight_divide_by?: number | string;
};

export function parseScaleSettings(value: unknown): ScaleBarcodeSettings | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return parseScaleSettings(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return typeof value === 'object' ? (value as ScaleBarcodeSettings) : null;
}

export function parseScaleBarcode(barcode: string, s: ScaleBarcodeSettings) {
  const sub = (start: unknown, digits: unknown) => barcode.substr(num(start) - 1, num(digits));
  const item_code = sub(s.item_code_start, s.item_code_digits);
  let price = 0;
  let weight = 0;
  if (s.type === 'price') {
    price = num(sub(s.price_start, s.price_digits));
    price = num(s.price_divide_by) ? price / num(s.price_divide_by) : price;
  } else {
    weight = num(sub(s.weight_start, s.weight_digits));
    weight = num(s.weight_divide_by) ? weight / num(s.weight_divide_by) : weight;
  }
  return { item_code, price, weight };
}

/** Number of serials described by purchase ranges (ItemSerialComponent#provided). */
export function countSerialRanges(ranges: SerialRange[]) {
  return ranges.reduce((a, r) => {
    if (!r.number && !r.till) return a;
    if (r.number && !r.till) return a + 1;
    const n = num(r.till) - num(r.number);
    return a + (n > 0 ? n + 1 : n === 0 ? 1 : 0);
  }, 0);
}
